import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface Configuration {
  id: number;
  key: string;
  value: string;
  status: boolean;
}

export default function ContactConfiguration() {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getConfigurations();
  }, [page, limit, search]);

  const getConfigurations = async () => {
    setLoading(true);
    const response = await request.post("contact-configurations", {
      search: search,
      limit,
      offset: page,
      orderColumn: "key",
      orderDirection: "desc",
    });
    setConfigurations(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "key",
      label: "Key",
      minWidth: 200,
      format: (value) => <Box component="span" sx={{ fontFamily: "monospace", color: "#667eea" }}>{value}</Box>,
    },
    {
      id: "value",
      label: "Value",
      minWidth: 250,
      format: (value) => value || "—",
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/contact-configuration/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.key }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Contact Configuration"
        showSearch
        searchPlaceholder="Search configurations..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Configuration",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/contact-configuration/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={configurations}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
          enableRowDrag={false}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Configuration - " + deleteDialog?.title}
          message="Are you sure you want to delete this configuration? This action cannot be undone."
          apiEndpoint={"contact-configurations/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getConfigurations();
          }}
        />
      </PageWrapper>
    </>
  );
}